import isEmpty from 'lodash/isEmpty';

import { VARIANTS } from '@jobcase/react_pattern_library/components/Toaster/Toaster';

import entity from './entity';
import { setNotification } from '../../notification/actions';
import profilesActions from '../profiles/actions';
import { getProfileKey } from '../../authentication/selectors';

const TYPES = {
  ...entity.getTypes(),
  GET_HUBS_REQUEST: 'hubs/GET_HUBS_REQUEST',
  GET_HUBS_SUCCESS: 'hubs/GET_HUBS_SUCCESS',
  GET_HUBS_FAILURE: 'hubs/GET_HUBS_FAILURE',
  CREATE_HUB_REQUEST: 'hubs/CREATE_HUB_REQUEST',
  CREATE_HUB_SUCCESS: 'hubs/CREATE_HUB_SUCCESS',
  CREATE_HUB_FAILURE: 'hubs/CREATE_HUB_FAILURE',
  CLEAR_NEW_HUB_ID: 'hubs/CLEAR_NEW_HUB_ID',
  CLEAR_STATE: 'hubs/CLEAR_STATE',
  INCREMENT_TOTAL_HUBS: 'hubs/INCREMENT_TOTAL_HUBS',
  RESET_NEW_HUB_ERROR: 'hubs/RESET_NEW_HUB_ERROR',
  GET_SPONSORSHIP_REQUEST: 'hubs/GET_SPONSORSHIP_REQUEST',
  GET_SPONSORSHIP_SUCCESS: 'hubs/GET_SPONSORSHIP_SUCCESS',
  GET_SPONSORSHIP_FAILURE: 'hubs/GET_SPONSORSHIP_FAILURE',
};

const getHub = (options, shouldAddToRedux = true) => async (dispatch) => {
  dispatch({ type: TYPES.GET_HUBS_REQUEST, payload: { page: 1 } });
  try {
    const response = await entity.api.getHub(options);
    const data = response?.data;
    const hubId = options?.hubId;
    const profiles = data?.official_profiles;
    const profileIds = profiles?.map(({ profile_key: profileKey }) => profileKey);
    dispatch(profilesActions.getAllCompact({ data: profileIds }));
    const payload = shouldAddToRedux ? { [hubId]: data } : {};
    dispatch({ type: TYPES.GET_REQUEST_SUCCESS, payload });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_REQUEST_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading the hub. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading the hub.', VARIANTS.INFO));
    }
    throw error;
  }
};

const createHub = data => async (dispatch) => {
  dispatch({ type: TYPES.CREATE_HUB_REQUEST });
  try {
    const response = await entity.api.createHub(data);
    const hubData = response?.data;
    dispatch({ type: TYPES.CREATE_HUB_SUCCESS, payload: { [hubData.id]: hubData, id: hubData.id } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.CREATE_HUB_FAILURE, payload: error });

    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while creating hub. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (!error.message.includes(`The given slug value '${data.data.slug}' is already in use`)) {
      dispatch(setNotification('An error occurred while creating hub.', VARIANTS.INFO));
    }
    throw error;
  }
};

const updateHub = data => async (dispatch, getState) => {
  const state = getState();
  const profileKey = getProfileKey(state);
  const headers = (profileKey != null) ? { 'X-Profile-Key': profileKey, 'X-Check-Roles': true } : {};

  dispatch({ type: TYPES.UPDATE_REQUEST });
  try {
    const response = await entity.api.updateHub(data, headers);
    const hub = response?.data;
    dispatch({ type: TYPES.UPDATE_REQUEST_SUCCESS, payload: { [hub.id]: hub } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.UPDATE_REQUEST_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while updating the hub. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while updating the hub.', VARIANTS.INFO));
    }
    throw error;
  }
};

const deleteHub = hubId => async (dispatch) => {
  try {
    const response = await entity.api.deleteHub(hubId);
    await dispatch({ type: TYPES.DESTROY_REQUEST_SUCCESS, payload: { [hubId]: null } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.DESTROY_REQUEST_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while deleting the hub. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while deleting the hub.', VARIANTS.INFO));
    }
    throw error;
  }
};

const getAllHubs = (options, profileKey) => async (dispatch) => {
  dispatch({ type: TYPES.GET_HUBS_REQUEST, payload: { page: options.page_index } });
  try {
    const response = await entity.api.getHubs(options, profileKey);
    const data = response?.data;
    const content = data?.content || [];
    const hubList = content.reduce((result, entity) => ({ ...result, [entity.id]: entity }), {});
    data.content = hubList;
    dispatch({ type: TYPES.GET_HUBS_SUCCESS, payload: data });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_HUBS_FAILURE, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading hubs. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading hubs.', VARIANTS.INFO));
    }
    throw error;
  }
};

const clearNewHubId = () => ({ type: TYPES.CLEAR_NEW_HUB_ID });

const clearState = () => (dispatch) => {
  dispatch({ type: TYPES.CLEAR_STATE });
};

const incrementTotalHubsCount = () => ({ type: TYPES.INCREMENT_TOTAL_HUBS });

const resetNewHubError = () => ({ type: TYPES.RESET_NEW_HUB_ERROR });

const getSponsorshipDetails = options => async (dispatch) => {
  dispatch({ type: TYPES.GET_SPONSORSHIP_REQUEST });
  try {
    const response = await entity.api.getHub(options);
    const data = response?.data?.sponsorships;
    dispatch({ type: TYPES.GET_SPONSORSHIP_SUCCESS, payload: data });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_SPONSORSHIP_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading the sponsorship details. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading the sponsorship details.', VARIANTS.INFO));
    }
    throw error;
  }
};

export {
  TYPES,
  updateHub,
  getHub,
  clearState,
  getAllHubs,
};

export default {
  TYPES,
  ...entity.getActions(),
  clearState,
  getHub,
  getAllHubs,
  createHub,
  updateHub,
  clearNewHubId,
  deleteHub,
  incrementTotalHubsCount,
  resetNewHubError,
  getSponsorshipDetails,
};
